import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import api from '../../../lib/api';

const exerciseLibrary = [
  { id: 1, name: 'Bench Press', category: 'Chest', muscle: 'Pectorals' },
  { id: 2, name: 'Squat', category: 'Legs', muscle: 'Quadriceps' },
  { id: 3, name: 'Deadlift', category: 'Back', muscle: 'Hamstrings' },
  { id: 4, name: 'Pull-Up', category: 'Back', muscle: 'Latissimus' },
  { id: 5, name: 'Shoulder Press', category: 'Shoulders', muscle: 'Deltoids' },
  { id: 6, name: 'Bicep Curl', category: 'Arms', muscle: 'Biceps' },
  { id: 7, name: 'Tricep Dip', category: 'Arms', muscle: 'Triceps' },
  { id: 8, name: 'Leg Press', category: 'Legs', muscle: 'Quadriceps' },
  { id: 9, name: 'Treadmill Run', category: 'Cardio', muscle: 'Full Body' },
  { id: 10, name: 'Cycling', category: 'Cardio', muscle: 'Legs' },
];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function WorkoutPlanBuilder() {
  const [plans, setPlans] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [view, setView] = useState('list'); // 'list' | 'create'
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedDay, setSelectedDay] = useState('Mon');
  const [showLibrary, setShowLibrary] = useState(false);
  const [libSearch, setLibSearch] = useState('');
  const [newPlan, setNewPlan] = useState({ title: '', member_id: '', exercises: {} });
  const [assigningPlanId, setAssigningPlanId] = useState(null);
  const [assignMemberId, setAssignMemberId] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [plansRes, membersRes] = await Promise.all([
        api.get('/workout-plans'),
        api.get('/members', { params: { status: 'active', limit: 2000 } }),
      ]);
      setPlans(plansRes?.data || []);
      setMembers(membersRes?.data || []);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const currentExercises = newPlan?.exercises?.[selectedDay] || [];

  const addExercise = (ex) => {
    setNewPlan(prev => ({
      ...prev,
      exercises: {
        ...prev?.exercises,
        [selectedDay]: [...(prev?.exercises?.[selectedDay] || []), { ...ex, sets: 3, reps: 10, weight: '0kg' }],
      },
    }));
    setShowLibrary(false);
  };

  const removeExercise = (idx) => {
    setNewPlan(prev => ({
      ...prev,
      exercises: {
        ...prev?.exercises,
        [selectedDay]: prev?.exercises?.[selectedDay]?.filter((_, i) => i !== idx),
      },
    }));
  };

  const updateExercise = (idx, field, value) => {
    setNewPlan(prev => ({
      ...prev,
      exercises: {
        ...prev?.exercises,
        [selectedDay]: prev?.exercises?.[selectedDay]?.map((ex, i) => i === idx ? { ...ex, [field]: value } : ex),
      },
    }));
  };

  const handleCreatePlan = async () => {
    if (!newPlan?.title?.trim()) { setError('Plan title is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const activeDays = Object.keys(newPlan?.exercises)?.filter(d => newPlan?.exercises?.[d]?.length > 0);
      const exercisesArray = activeDays?.map(day => ({ day, exercises: newPlan?.exercises?.[day] }));
      await api.post('/workout-plans', {
        member_id: newPlan?.member_id || null,
        title: newPlan?.title?.trim(),
        days: activeDays,
        exercises: exercisesArray,
      });
      setSuccess('Workout plan created successfully!');
      setTimeout(() => setSuccess(null), 3000);
      setNewPlan({ title: '', member_id: '', exercises: {} });
      setView('list');
      fetchData();
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAssignPlan = async (planId, memberId) => {
    if (!memberId) return;
    setAssigningPlanId(planId);
    try {
      await api.put(`/workout-plans/${planId}`, { member_id: memberId });
      setSuccess('Plan assigned successfully!');
      setTimeout(() => setSuccess(null), 3000);
      fetchData();
    } catch (err) {
      setError(err?.message);
    } finally {
      setAssigningPlanId(null);
      setAssignMemberId('');
    }
  };

  const filteredLib = exerciseLibrary?.filter((e) =>
    e?.name?.toLowerCase()?.includes(libSearch?.toLowerCase()) ||
    e?.category?.toLowerCase()?.includes(libSearch?.toLowerCase())
  );

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)]">Workout Plan Builder</h3>
        <button
          onClick={() => setView(view === 'list' ? 'create' : 'list')}
          className="flex items-center gap-2 px-3 py-1.5 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-xs font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity"
        >
          <Icon name={view === 'list' ? 'Plus' : 'List'} size={13} color="#fff" />
          {view === 'list' ? 'New Plan' : 'View Plans'}
        </button>
      </div>
      {error && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-[var(--radius-sm)]">
          <Icon name="AlertCircle" size={16} color="var(--color-error)" />
          <p className="text-sm text-[var(--color-error)] font-[var(--font-caption)]">{error}</p>
        </div>
      )}
      {success && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-[var(--color-success)]/10 border border-[var(--color-success)]/20 rounded-[var(--radius-sm)]">
          <Icon name="CheckCircle" size={16} color="var(--color-success)" />
          <p className="text-sm text-[var(--color-success)] font-[var(--font-caption)]">{success}</p>
        </div>
      )}
      {view === 'list' ? (
        loading ? (
          <div className="flex flex-col gap-3">
            {[1, 2, 3]?.map(i => <div key={i} className="h-16 bg-[var(--color-muted)] rounded animate-pulse" />)}
          </div>
        ) : plans?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <Icon name="Dumbbell" size={32} color="var(--color-muted-foreground)" />
            <p className="mt-2 text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No workout plans yet</p>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Click "New Plan" to create one</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {plans?.map((plan) => (
              <div key={plan?.id} className="p-4 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] hover:border-[var(--color-primary)]/30 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] truncate">{plan?.title}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      {plan?.trainer_name && (
                        <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                          <Icon name="User" size={11} color="currentColor" className="inline mr-0.5" />
                          {plan?.trainer_name}
                        </span>
                      )}
                      {plan?.member_name && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium font-[var(--font-caption)]">
                          {plan?.member_name}
                        </span>
                      )}
                      {plan?.days?.length > 0 && (
                        <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                          {plan?.days?.join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedPlan(selectedPlan?.id === plan?.id ? null : plan)}
                    className="p-1.5 rounded hover:bg-[var(--color-muted)] transition-colors flex-shrink-0"
                  >
                    <Icon name={selectedPlan?.id === plan?.id ? 'ChevronUp' : 'ChevronDown'} size={14} color="var(--color-muted-foreground)" />
                  </button>
                </div>

                {selectedPlan?.id === plan?.id && (
                  <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
                    {/* Exercise breakdown */}
                    {plan?.exercises?.length > 0 && (
                      <div className="mb-3">
                        {plan?.exercises?.map((dayPlan, di) => (
                          <div key={di} className="mb-2">
                            <p className="text-xs font-semibold text-[var(--color-primary)] font-[var(--font-caption)] mb-1">{dayPlan?.day}</p>
                            <div className="flex flex-wrap gap-1">
                              {dayPlan?.exercises?.map((ex, ei) => (
                                <span key={ei} className="text-xs px-2 py-0.5 bg-[var(--color-muted)] rounded text-[var(--color-foreground)] font-[var(--font-caption)]">
                                  {ex?.name} {ex?.sets}×{ex?.reps}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Assign to member */}
                    <div className="flex items-center gap-2">
                      <select
                        value={assignMemberId}
                        onChange={e => setAssignMemberId(e?.target?.value)}
                        className="flex-1 px-2 py-1.5 bg-[var(--color-card)] border border-[var(--color-border)] rounded text-xs text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]"
                      >
                        <option value="">Assign to member...</option>
                        {members?.map(m => <option key={m?.id} value={m?.id}>{m?.first_name} {m?.last_name}</option>)}
                      </select>
                      <button
                        onClick={() => handleAssignPlan(plan?.id, assignMemberId)}
                        disabled={!assignMemberId || assigningPlanId === plan?.id}
                        className="px-3 py-1.5 bg-[var(--color-primary)] text-white rounded text-xs font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity disabled:opacity-50"
                      >
                        {assigningPlanId === plan?.id ? 'Assigning...' : 'Assign'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : (
        /* Create Plan Form */
        (<div className="flex flex-col gap-4">
          <div>
            <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1.5">Plan Title *</label>
            <input
              type="text"
              value={newPlan?.title}
              onChange={e => setNewPlan(p => ({ ...p, title: e?.target?.value }))}
              placeholder="e.g. Strength Builder Program"
              className="w-full px-3 py-2.5 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-caption)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1.5">Assign to Member</label>
            <select
              value={newPlan?.member_id}
              onChange={e => setNewPlan(p => ({ ...p, member_id: e?.target?.value }))}
              className="w-full px-3 py-2 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
            >
              <option value="">Select member (optional)</option>
              {members?.map(m => <option key={m?.id} value={m?.id}>{m?.first_name} {m?.last_name}</option>)}
            </select>
          </div>
          {/* Day Selector */}
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {DAYS?.map((d) => (
              <button
                key={d}
                onClick={() => setSelectedDay(d)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-[var(--radius-sm)] text-xs font-medium font-[var(--font-caption)] transition-all duration-200 relative ${
                  selectedDay === d ? 'bg-[var(--color-primary)] text-white' : 'bg-[var(--color-muted)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]'
                }`}
              >
                {d}
                {newPlan?.exercises?.[d]?.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-[var(--color-accent)] rounded-full text-[9px] text-white flex items-center justify-center font-bold">
                    {newPlan?.exercises?.[d]?.length}
                  </span>
                )}
              </button>
            ))}
          </div>
          {/* Add Exercise Button */}
          <button
            onClick={() => setShowLibrary(!showLibrary)}
            className="flex items-center gap-2 px-3 py-2 border border-dashed border-[var(--color-primary)]/40 rounded-[var(--radius-sm)] text-xs font-medium text-[var(--color-primary)] font-[var(--font-caption)] hover:bg-[var(--color-primary)]/5 transition-colors"
          >
            <Icon name="Plus" size={13} color="var(--color-primary)" />
            Add Exercise for {selectedDay}
          </button>
          {/* Exercise Library */}
          {showLibrary && (
            <div className="border border-[var(--color-border)] rounded-[var(--radius-sm)] overflow-hidden">
              <div className="p-2 border-b border-[var(--color-border)] bg-[var(--color-muted)]">
                <input
                  type="text"
                  value={libSearch}
                  onChange={(e) => setLibSearch(e?.target?.value)}
                  placeholder="Search exercises..."
                  className="w-full px-3 py-1.5 bg-[var(--color-background)] border border-[var(--color-border)] rounded text-sm text-[var(--color-foreground)] font-[var(--font-caption)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
                />
              </div>
              <div className="max-h-40 overflow-y-auto">
                {filteredLib?.map((ex) => (
                  <button
                    key={ex?.id}
                    onClick={() => addExercise(ex)}
                    className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[var(--color-muted)] transition-colors text-left"
                  >
                    <div>
                      <p className="text-sm font-medium text-[var(--color-foreground)] font-[var(--font-caption)]">{ex?.name}</p>
                      <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{ex?.muscle}</p>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium font-[var(--font-caption)]">{ex?.category}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Current Day Exercises */}
          {currentExercises?.length > 0 && (
            <div className="flex flex-col gap-2">
              {currentExercises?.map((ex, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] group">
                  <div className="w-8 h-8 rounded-[var(--radius-sm)] bg-[var(--color-primary)]/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="Dumbbell" size={15} color="var(--color-primary)" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] truncate">{ex?.name}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <label className="flex items-center gap-1 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                        Sets:
                        <input type="number" value={ex?.sets} onChange={(e) => updateExercise(idx, 'sets', e?.target?.value)}
                          className="w-10 px-1 py-0.5 bg-[var(--color-card)] border border-[var(--color-border)] rounded text-xs text-[var(--color-foreground)] font-[var(--font-caption)] text-center focus:outline-none focus:border-[var(--color-primary)]" />
                      </label>
                      <label className="flex items-center gap-1 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                        Reps:
                        <input type="number" value={ex?.reps} onChange={(e) => updateExercise(idx, 'reps', e?.target?.value)}
                          className="w-10 px-1 py-0.5 bg-[var(--color-card)] border border-[var(--color-border)] rounded text-xs text-[var(--color-foreground)] font-[var(--font-caption)] text-center focus:outline-none focus:border-[var(--color-primary)]" />
                      </label>
                      <label className="flex items-center gap-1 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                        Wt:
                        <input type="text" value={ex?.weight} onChange={(e) => updateExercise(idx, 'weight', e?.target?.value)}
                          className="w-14 px-1 py-0.5 bg-[var(--color-card)] border border-[var(--color-border)] rounded text-xs text-[var(--color-foreground)] font-[var(--font-caption)] text-center focus:outline-none focus:border-[var(--color-primary)]" />
                      </label>
                    </div>
                  </div>
                  <button onClick={() => removeExercise(idx)} className="opacity-0 group-hover:opacity-100 p-1.5 rounded hover:bg-[var(--color-error)]/10 transition-all">
                    <Icon name="Trash2" size={14} color="var(--color-error)" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <button
            onClick={handleCreatePlan}
            disabled={saving || !newPlan?.title?.trim()}
            className="flex items-center justify-center gap-2 py-2.5 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Icon name="Loader2" size={14} color="#fff" className="animate-spin" /> : <Icon name="Save" size={14} color="#fff" />}
            {saving ? 'Saving...' : 'Save Workout Plan'}
          </button>
        </div>)
      )}
    </div>
  );
}
